import type { AccountType } from '../../types/account.js';
import type { PortalAssetReviewStatus } from '../../types/portalAsset.js';
import { PORTAL_OPERATIONAL_TYPES } from '../portalScope.js';

// The client assets pages' URL state and account list (commit 12). PURE.
//
// ⚠️ The account picker offers OPERATIONAL accounts only (locations and
// dealerships): an asset lives on one, and a group the viewer can select for a
// report is not somewhere a photo can be uploaded. The server enforces the same
// rule (scope.accountIds); this only keeps the picker from offering a choice the
// server would refuse.
//
// ⚠️ Everything read back from the URL is VALIDATED: a URL is editable and
// forwardable, and a stale `?status=` must not break the page. An unknown
// account id is NOT dropped here — it goes to the server, which answers 404, and
// the page says the location is not one this login can see. Silently falling
// back to "all locations" would answer a question nobody asked.

export type AssetAccountOption = { id: string; name: string };

export function operationalAccounts(
  accounts: readonly { id: string; name: string; accountType: AccountType }[],
): AssetAccountOption[] {
  return accounts
    .filter((a) => PORTAL_OPERATIONAL_TYPES.includes(a.accountType))
    .map((a) => ({ id: a.id, name: a.name }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

const STATUSES: readonly PortalAssetReviewStatus[] = ['pending', 'approved', 'rejected'];

export function parseStatusFilter(raw: string | null): PortalAssetReviewStatus | undefined {
  return raw !== null && (STATUSES as readonly string[]).includes(raw) ? (raw as PortalAssetReviewStatus) : undefined;
}

export const STATUS_FILTER_LABELS: Record<PortalAssetReviewStatus, string> = {
  pending: 'Waiting for review',
  approved: 'Approved',
  rejected: 'Not approved',
};

function withQuery(path: string, q: Record<string, string | undefined>): string {
  const s = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) if (v) s.set(k, v);
  const qs = s.toString();
  return qs ? `${path}?${qs}` : path;
}

export function assetsListHref(q: { account?: string; status?: string } = {}): string {
  return withQuery('/portal/assets', q);
}

export function assetsUploadHref(account?: string): string {
  return withQuery('/portal/assets/upload', { account });
}

export function assetDetailHref(assetId: string): string {
  return `/portal/assets/${encodeURIComponent(assetId)}`;
}

/**
 * Which account an upload goes to. With exactly one operational account it is
 * that one; with several it must be CHOSEN — never defaulted to the first, which
 * would put a photo on the wrong club with nothing on screen to say so.
 */
export function uploadAccount(options: readonly AssetAccountOption[], requested: string | null): string | null {
  if (options.length === 1) return options[0]!.id;
  return requested !== null && options.some((o) => o.id === requested) ? requested : null;
}

export function emptyListSentence(filtered: boolean): string {
  return filtered
    ? 'Nothing matches this filter.'
    : 'Nothing here yet. Upload photos and videos from your club, and Kyck will review them before they are used.';
}
